import React, { useState, useEffect } from 'react';
import syncTemplatesService from '../services/syncTemplatesService';
import ConfirmModal from './shared/ConfirmModal';
import './AdminSyncTemplates.css';
import './PaymentPorterPage.css';

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];

const emptyScope = { scopeType: '', description: '', status: 'ACTIVE', category: '', subCategory: '', sequence: '', iconUrl: '', publishYn: 'N' };

const AdminSyncScopes = () => {
    const [scopes, setScopes]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    const [form, setForm]       = useState(null);
    const [editId, setEditId]   = useState(null);
    const [saving, setSaving]   = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [search, setSearch]   = useState('');

    const load = () =>
        syncTemplatesService.listScopes()
            .then(data => setScopes(data || []))
            .catch(err => setError(err.response?.data?.message || err.message || 'Failed to load'));

    useEffect(() => {
        load().finally(() => setLoading(false));
    }, []); // eslint-disable-line

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const openEdit = (s) => {
        setForm({
            scopeType:   s.scopeType || '',
            description: s.description || '',
            status:      s.status || 'ACTIVE',
            category:    s.category ?? '',
            subCategory: s.subCategory || '',
            sequence:    s.sequence ?? '',
            iconUrl:     s.iconUrl || '',
            publishYn:   s.publishYn || 'N',
        });
        setEditId(s.id);
    };

    const handleSave = async () => {
        if (!form.scopeType.trim()) return;
        setSaving(true);
        setError(null);
        try {
            if (editId === null) {
                await syncTemplatesService.createScope(form);
            } else {
                await syncTemplatesService.updateScope(editId, form);
            }
            await load();
            setForm(null);
            setEditId(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm) return;
        setError(null);
        try {
            await syncTemplatesService.deleteScope(deleteConfirm.id);
            setScopes(prev => prev.filter(s => s.id !== deleteConfirm.id));
        } catch (err) {
            setError(err.response?.data?.message || 'Delete failed');
        } finally {
            setDeleteConfirm(null);
        }
    };

    const filtered = scopes.filter(s => {
        const q = search.toLowerCase();
        return !q || (s.scopeType || '').toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q);
    });

    if (loading) return <div className="ast-container"><div className="ast-loading">Loading sync scopes…</div></div>;

    return (
        <div className="ast-container">
            <div className="ast-header">
                <h1>Sync Scopes</h1>
                <button className="pp-rules-add-btn" onClick={() => { setForm({ ...emptyScope }); setEditId(null); }}>
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    New Scope
                </button>
            </div>

            <p className="ast-page-hint">
                Scope types link incoming message data types to sync template tasks — a message whose data type matches a scope type is
                reconciled under the task that references that scope.
            </p>

            {error && <div className="ast-error">{error}</div>}

            <div className="tenant-search-container" style={{maxWidth:'340px', marginBottom:'14px'}}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tenant-search-icon"><circle cx="7" cy="7" r="4" stroke="#6b7280" strokeWidth="2"/><path d="M10 10l3 3" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg>
                <input type="text" className="tenant-search-input" placeholder="Search scopes…" value={search} onChange={e => setSearch(e.target.value)} />
                {search && <button type="button" className="search-clear-btn" onClick={() => setSearch('')}><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/></svg></button>}
            </div>

            <table className="ast-tasks-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Scope Type</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Used By</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.length === 0 ? (
                        <tr><td colSpan={6} className="ast-no-tasks">{search ? 'No scopes match the search.' : 'No scopes yet.'}</td></tr>
                    ) : (
                        filtered.map(s => {
                            const inUse = (s.taskCount || 0) + (s.projectCount || 0) > 0;
                            return (
                                <tr key={s.id}>
                                    <td>{s.id}</td>
                                    <td><span className="ast-template-code">{s.scopeType}</span></td>
                                    <td>{s.description || '—'}</td>
                                    <td><span className={`ast-badge ast-badge-${(s.status || 'ACTIVE').toLowerCase()}`}>{s.status || 'ACTIVE'}</span></td>
                                    <td>
                                        {inUse
                                            ? [s.taskCount > 0 && `${s.taskCount} task${s.taskCount > 1 ? 's' : ''}`,
                                               s.projectCount > 0 && `${s.projectCount} project${s.projectCount > 1 ? 's' : ''}`]
                                                .filter(Boolean).join(', ')
                                            : '—'}
                                    </td>
                                    <td>
                                        <div className="pp-criteria-row-actions">
                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary pp-criteria-add-btn-icon" title="Edit" onClick={() => openEdit(s)}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            </button>
                                            <button
                                                className="pp-criteria-add-btn pp-criteria-add-btn-danger pp-criteria-add-btn-icon"
                                                title={inUse ? 'Scope is in use and cannot be deleted' : 'Delete'}
                                                disabled={inUse}
                                                onClick={() => !inUse && setDeleteConfirm({ id: s.id, scopeType: s.scopeType })}
                                            >
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>

            {form && (
                <div className="ast-task-form-panel">
                    <h4 className="ast-task-form-title">{editId === null ? 'New Scope' : `Edit Scope ${editId}`}</h4>

                    <div className="ast-form-row">
                        <div className="ast-form-group">
                            <label>Scope Type <span style={{color:'#dc2626'}}>*</span></label>
                            <input value={form.scopeType} onChange={e => set('scopeType', e.target.value.toUpperCase())} placeholder="e.g. PEP-ROOM" />
                        </div>
                        <div className="ast-form-group">
                            <label>Status</label>
                            <select value={form.status} onChange={e => set('status', e.target.value)}>
                                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="ast-form-group">
                        <label>Description</label>
                        <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Room Configuration File" />
                    </div>
                    <div className="ast-form-row">
                        <div className="ast-form-group">
                            <label>Category</label>
                            <input type="number" value={form.category} onChange={e => set('category', e.target.value)} />
                        </div>
                        <div className="ast-form-group">
                            <label>Sub Category</label>
                            <input value={form.subCategory} onChange={e => set('subCategory', e.target.value)} />
                        </div>
                        <div className="ast-form-group">
                            <label>Sequence</label>
                            <input type="number" value={form.sequence} onChange={e => set('sequence', e.target.value)} />
                        </div>
                    </div>
                    <div className="ast-form-row">
                        <div className="ast-form-group">
                            <label>Icon URL</label>
                            <input value={form.iconUrl} onChange={e => set('iconUrl', e.target.value)} placeholder="https://images.hconnect.io/icons/…" />
                        </div>
                        <div className="ast-form-group">
                            <label>Published</label>
                            <select value={form.publishYn} onChange={e => set('publishYn', e.target.value)}>
                                <option value="N">No</option>
                                <option value="Y">Yes</option>
                            </select>
                        </div>
                    </div>

                    <div className="ast-form-actions">
                        <button className="pp-criteria-add-btn" onClick={() => { setForm(null); setEditId(null); }} disabled={saving}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                            Cancel
                        </button>
                        <button className="pp-rules-add-btn" onClick={handleSave} disabled={saving || !form.scopeType.trim()}>
                            {saving
                                ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>Saving…</>
                                : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>{editId === null ? 'Create' : 'Save'}</>
                            }
                        </button>
                    </div>
                </div>
            )}

            {deleteConfirm && (
                <ConfirmModal
                    title="Delete Scope"
                    message={`Delete scope ${deleteConfirm.scopeType}?`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeleteConfirm(null)}
                />
            )}
        </div>
    );
};

export default AdminSyncScopes;
